import React, { useState, useEffect, useId, useRef } from 'react';

// MUI Components
import { TextField, Autocomplete, Divider, Typography, Box, Checkbox, Chip } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';

// Icons
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBoxOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOffOutlined';
import { CrossIconCIcon, SelectCloseCIcon } from '~/assets/images';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';

// styles
import './styles.scss';

const GenericAutocomplete = (props) => {
  const {
    placeholder,
    options,
    selectedValue,
    setSelectedValue,
    search,
    updateErrors,
    name,
    loading,
    disable = false,
    multiple = false,
    disableClearable = false,
    templateBuilder = false,
    filterAutoComplete = false,
    error = false,
    tagsClass = false,
    campaign = false,
    className = '',
    newDesign = false,
    listClass = '',
    dropdownType = '',
    persistSearch = false,
    searchValue = '',
    readOnlyInput = false,
    hideClearIcon = false,
    hideDividers = false,
    nativeScrollbar = false,
    menuClassName = '',
  } = props;
  const [menuOpen, setMenuOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const lastInputValueRef = useRef('');
  const autocompleteId = useId();

  // Sync inputValue with searchValue when persistSearch is enabled
  useEffect(() => {
    if (persistSearch) {
      // Only sync if searchValue is different from what we have (user typed something new)
      // This prevents resetting when options are selected
      if (searchValue !== lastInputValueRef.current) {
        setInputValue(searchValue || '');
        lastInputValueRef.current = searchValue || '';
      }
    }
  }, [searchValue, persistSearch]);

  const updateValue = (event, newValue) => {
    event.preventDefault();
    !newValue?.func ? setSelectedValue(newValue) : undefined;
    updateErrors((prev) => ({
      ...prev,
      [name]: false,
    }));
    newValue?.func ? newValue?.func() : undefined;
    if (!persistSearch) {
      search('');
    }
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = (
    <CheckBoxIcon
      fontSize="small"
      sx={{
        color: '#ED5C2F',
      }}
    />
  );

  const clearIcon = tagsClass ? null : selectedValue &&
    (Array.isArray(selectedValue) ? selectedValue.length > 0 : true) ? (
    // NOTE:
    // Do NOT attach onClick handlers directly to this element.
    // MUI wraps `clearIcon` with an IconButton which already handles clearing the value.
    // Adding an onClick here causes the "child of a button element" prop-type warning.
    <img
      src={newDesign ? SelectCloseCIcon : CrossIconCIcon}
      style={{
        top: '5px',
        width: newDesign ? '11px' : 'inherit',
        height: newDesign ? '11px' : 'inherit',
      }}
      alt="clear"
    />
  ) : null;

  const renderOption = (props, option, { selected }) => {
    return (
      <div key={option.id}>
        {loading && options.indexOf(option) === 0 ? (
          <Box>
            <LinearProgress
              sx={{
                backgroundColor: 'white',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#ED5C2F',
                },
              }}
            />
          </Box>
        ) : null}
        {dropdownType === 'campaign' ? (
          <>
            {option.custom ? (
              <>
                {options?.some((opt) => !opt.custom) && !option.hideDividerAbove && (
                  <Divider sx={{ my: 1, borderColor: 'rgba(0, 0, 0, 0.12)', width: '100%' }} />
                )}
                <li {...props} key={option.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    {option.icon && <img src={option.icon} alt="icon" />}
                    <Typography
                      className="campaignDropdownNewBtn"
                      sx={{ color: option.custom ? '#ED5C2F' : '' }}
                    >
                      {option.label}
                    </Typography>
                  </Box>
                </li>
              </>
            ) : (
              <li {...props} key={option.id}>
                {option.custom ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                  >
                    {option.icon && <img src={option.icon} alt="icon" />}
                    <Typography sx={{ color: option.custom ? '#ED5C2F' : '' }}>
                      {option.label}
                    </Typography>
                  </Box>
                ) : multiple ? (
                  <>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected || selectedValue?.includes(option.label)}
                    />
                    {option.label}
                  </>
                ) : (
                  <Typography
                    className={`${templateBuilder ? 'tempBuilderOptions' : ''} ${listClass || ''}`}
                  >
                    {option.label}
                  </Typography>
                )}
              </li>
            )}
          </>
        ) : (
          <>
            {option.custom && !hideDividers && !option.hideDividerAbove ? (
              <Divider className="listDivider" sx={{ margin: '8px 0px !important' }} />
            ) : null}
            <li {...props} key={option.id}>
              {option.custom ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  {option.icon && <img src={option.icon} alt="icon" />}
                  <Typography
                    sx={{
                      color: option.custom ? '#ED5C2F !important' : '',
                      fontWeight: option.custom ? '400 !important' : '',
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              ) : multiple ? (
                <>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected || selectedValue?.includes(option.label)}
                  />
                  {option.label}
                </>
              ) : (
                <Typography className={templateBuilder && 'tempBuilderOptions'}>
                  {option.label}
                </Typography>
              )}
            </li>
          </>
        )}
        {className !== 'newAutocompleteDesign' && !hideDividers && (
          <Divider className="listDivider" />
        )}
      </div>
    );
  };

  const getAutocompleteClassName = (name) => {
    const componentNames = ['template'];
    const designClassName = componentNames.includes(name)
      ? 'autocompletePaper'
      : newDesign
        ? 'newAutocompleteMenuDesignPrimary'
        : '';

    return [designClassName, menuClassName].filter(Boolean).join(' ');
  };

  return (
    <Autocomplete
      className={
        multiple
          ? `multipleAutoSelect ${tagsClass && 'tagsUpdateSelect'}`
          : templateBuilder
            ? 'tempAutocomplete'
            : `genericAutocomplete ${filterAutoComplete ? 'filterAutoComplete' : ''} ${error ? 'errorAutocomplete' : ''} ${className}`
      }
      multiple={multiple}
      renderTags={(value, getTagProps) =>
        tagsClass
          ? null
          : value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.label}
                label={option.label}
                deleteIcon={<HighlightOffIcon sx={{ color: '#ED5C2F' }} />}
              />
            ))
      }
      clearIcon={null}
      sx={{
        maxWidth: campaign ? '100%' : '564px',
        background: campaign ? 'white' : 'transparent',
        '& .MuiAutocomplete-clearIndicator': {
          visibility: selectedValue ? 'visible' : 'hidden',
        },
      }}
      limitTags={multiple && tagsClass ? 3 : multiple ? 1 : undefined}
      disabled={disable}
      disableCloseOnSelect={multiple}
      disableClearable
      id={autocompleteId}
      isOptionEqualToValue={(option, value) => {
        const optLabel = typeof option === 'string' ? option : option.label;
        const valLabel = typeof value === 'string' ? value : value.label;
        return optLabel === valLabel;
      }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label || '')}
      value={selectedValue}
      loading={loading}
      inputValue={persistSearch ? inputValue : undefined}
      onInputChange={(event, newInputValue, reason) => {
        if (readOnlyInput) return;

        if (persistSearch) {
          if (reason === 'input') {
            setInputValue(newInputValue);
            lastInputValueRef.current = newInputValue;
            search(newInputValue);
          } else if (reason === 'reset') {
            setInputValue(lastInputValueRef.current);
          }
        } else if (reason === 'input' || reason === 'clear') {
          search(newInputValue || '');
        }
      }}
      onChange={(event, newValue) => {
        updateValue(event, newValue);
        if (!persistSearch) {
          !multiple ? search(newValue?.label ? newValue?.label : newValue) : undefined;
        }
      }}
      onKeyDown={(event) => {
        if (readOnlyInput) {
          // Prevent all keyboard input except Escape, Arrow keys, Tab, and function keys for navigation
          if (
            event.key !== 'Escape' &&
            !event.key.startsWith('Arrow') &&
            !event.key.startsWith('F') &&
            event.key !== 'Tab' &&
            event.key !== 'Enter'
          ) {
            event.preventDefault();
          }
        }
      }}
      onBlur={(event) => {
        if (!persistSearch && !readOnlyInput) {
          search(event.target.value || '');
        }
      }}
      onFocus={(event) => {
        if (!persistSearch && !readOnlyInput) {
          search(event.target.value || '');
        }
        if (event?.target?.setSelectionRange) {
          requestAnimationFrame(() => {
            const cursorPosition = event.target.value?.length || 0;
            event.target.setSelectionRange(cursorPosition, cursorPosition);
          });
        }
      }}
      options={options}
      onOpen={() => setMenuOpen(true)}
      onClose={() => setMenuOpen(false)}
      popupIcon={<OlcDropdownChevron disabled={disable} open={menuOpen} />}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={
            selectedValue?.length > 0 && tagsClass
              ? placeholder
              : selectedValue?.length > 0
                ? ''
                : placeholder
          }
          size="small"
          className={templateBuilder ? 'tempAutoField' : ''}
          inputProps={{
            ...params.inputProps,
            'aria-label': placeholder || name || 'Select option',
            readOnly: readOnlyInput,
            onFocus: (e) => {
              params.inputProps?.onFocus?.(e);
              if (readOnlyInput && selectedValue) {
                // Clear any text selection on focus - use requestAnimationFrame to not interfere with dropdown
                requestAnimationFrame(() => {
                  e.target.setSelectionRange(0, 0);
                });
              } else {
                requestAnimationFrame(() => {
                  const cursorPosition = e.target.value?.length || 0;
                  e.target.setSelectionRange(cursorPosition, cursorPosition);
                });
              }
            },
            onSelect: (e) => {
              params.inputProps?.onSelect?.(e);
              if (readOnlyInput && selectedValue) {
                // Clear selection if it happens
                requestAnimationFrame(() => {
                  e.target.setSelectionRange(0, 0);
                });
              } else {
                requestAnimationFrame(() => {
                  const cursorPosition = e.target.value?.length || 0;
                  e.target.setSelectionRange(cursorPosition, cursorPosition);
                });
              }
            },
          }}
          sx={
            readOnlyInput
              ? {
                  '& input': {
                    cursor: 'pointer !important',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    caretColor: 'transparent',
                    '&::selection': {
                      background: 'transparent',
                    },
                    '&::-moz-selection': {
                      background: 'transparent',
                    },
                  },
                }
              : undefined
          }
        />
      )}
      renderOption={renderOption}
      classes={{
        paper: [getAutocompleteClassName(name), tagsClass ? 'tagsPaper' : '']
          .filter(Boolean)
          .join(' '),
      }}
      getOptionDisabled={(option) => option.customPlaceholder}
      ListboxProps={{
        className: ['olcSingleSelectListbox', nativeScrollbar ? 'olcNativeScrollbar' : '']
          .filter(Boolean)
          .join(' '),
        style: { maxHeight: 250 },
      }}
    />
  );
};

export default GenericAutocomplete;
